/**
 * User Status Logs Page
 * Displays status change history for a specific user
 * Opens in a new tab from UserDetailPage
 */
import {
  faArrowLeft,
  faExclamationTriangle,
  faHistory,
  faRobot,
  faSpinner,
  faUser,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useUrlState } from '../hooks/useUrlState';
import {
  apiClient,
  type StatusLogAction,
  type StatusLogEntry,
  UserStatus
} from '../lib/api-client';
import { formatDateTime } from '../utils/datetime';

interface UserInfo {
  id: string;
  did: string;
  handle: string;
  displayName: string | null;
}

const ACTION_LABELS: Record<StatusLogAction, string> = {
  SUSPEND: '一時停止',
  BLOCK: 'ブロック',
  LIFT: '停止解除',
  AUTO_LIFT: '自動解除',
  RESTRICT: '制限',
  ACTIVATE: 'アクティブ化'
};

const ACTION_COLORS: Record<StatusLogAction, string> = {
  SUSPEND: 'text-yellow-600 bg-yellow-100',
  BLOCK: 'text-red-600 bg-red-100',
  LIFT: 'text-green-600 bg-green-100',
  AUTO_LIFT: 'text-blue-600 bg-blue-100',
  RESTRICT: 'text-orange-600 bg-orange-100',
  ACTIVATE: 'text-green-600 bg-green-100'
};

const STATUS_LABELS: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: 'アクティブ',
  [UserStatus.RESTRICTED]: '制限',
  [UserStatus.SUSPENDED]: '一時停止',
  [UserStatus.BLOCKED]: 'ブロック'
};

const STATUS_COLORS: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: 'text-green-600',
  [UserStatus.RESTRICTED]: 'text-orange-600',
  [UserStatus.SUSPENDED]: 'text-yellow-600',
  [UserStatus.BLOCKED]: 'text-red-600'
};

// 1 ページあたりの件数と先頭ページ番号（0 始まり）。
const PAGE_SIZE = 20;
const FIRST_PAGE = 0;
// URL に不正値が入った場合のフォールバック用の許可値。
const ACTION_FILTERS: readonly (StatusLogAction | '')[] = [
  '',
  ...(Object.keys(ACTION_LABELS) as StatusLogAction[])
];

export default function UserStatusLogsPage() {
  const { userId } = useParams<{ userId: string }>();
  const [logs, setLogs] = useState<StatusLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  // ページとフィルタは URL クエリで管理し、戻る / 進む と URL 共有に対応する
  const [{ page, action: filterAction }, setUrlState] = useUrlState(
    { page: FIRST_PAGE, action: '' as StatusLogAction | '' },
    { action: ACTION_FILTERS }
  );

  useEffect(() => {
    if (userId) {
      fetchUserInfo();
      fetchLogs();
    }
  }, [userId, page, filterAction]);

  const fetchUserInfo = async () => {
    if (!userId) return;
    try {
      const user = await apiClient.getUser(userId);
      setUserInfo({
        id: user.id,
        did: user.did,
        handle: user.handle,
        displayName: user.display_name
      });
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  };

  const fetchLogs = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);

    try {
      const result = await apiClient.getUserStatusLogs(userId, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        action: filterAction || undefined
      });
      setLogs(result.logs);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getPerformerIcon = (performerType: string) => {
    switch (performerType) {
      case 'SYSTEM':
        return <FontAwesomeIcon icon={faRobot} className='text-blue-500' />;
      case 'ADMIN':
        return <FontAwesomeIcon icon={faUserShield} className='text-purple-500' />;
      case 'USER':
        return <FontAwesomeIcon icon={faUser} className='text-gray-500' />;
      default:
        return <FontAwesomeIcon icon={faUser} className='text-gray-400' />;
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className='container mx-auto px-4 py-6'>
      {/* Header */}
      <div className='mb-6'>
        <div className='mb-4 flex items-center gap-4'>
          <Link
            to={`/users/${userId}`}
            className='text-gray-600 transition-colors hover:text-gray-800'
          >
            <FontAwesomeIcon icon={faArrowLeft} className='mr-2' />
            ユーザー詳細に戻る
          </Link>
        </div>

        <div className='flex items-center gap-3'>
          <FontAwesomeIcon icon={faHistory} className='text-2xl text-indigo-600' />
          <div>
            <h1 className='text-2xl font-bold text-gray-800'>ステータス変更履歴</h1>
            {userInfo && (
              <p className='text-gray-600'>
                @{userInfo.handle}
                {userInfo.displayName && ` (${userInfo.displayName})`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className='mb-6 rounded-lg bg-white p-4 shadow'>
        <label className='mb-2 block text-sm font-medium text-gray-700'>アクションで絞り込み</label>
        <select
          value={filterAction}
          onChange={e =>
            setUrlState({
              action: e.target.value as StatusLogAction | '',
              page: FIRST_PAGE
            })
          }
          className='rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500'
        >
          <option value=''>すべて</option>
          {Object.entries(ACTION_LABELS).map(([action, label]) => (
            <option key={action} value={action}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className='mb-6 rounded-lg border border-red-200 bg-red-50 p-4'>
          <div className='flex items-center gap-2 text-red-700'>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className='flex items-center justify-center py-12'>
          <FontAwesomeIcon icon={faSpinner} className='animate-spin text-3xl text-indigo-600' />
        </div>
      )}

      {/* Empty State */}
      {!loading && logs.length === 0 && (
        <div className='rounded-lg bg-white p-8 text-center shadow'>
          <FontAwesomeIcon icon={faHistory} className='mb-4 text-4xl text-gray-400' />
          <p className='text-gray-600'>ステータス変更履歴がありません</p>
        </div>
      )}

      {/* Logs Table */}
      {!loading && logs.length > 0 && (
        <div className='overflow-hidden rounded-lg bg-white shadow'>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    日時
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    アクション
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    変更前
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    変更後
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    停止期限
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    理由
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    実行者
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 bg-white'>
                {logs.map(log => (
                  <tr key={log.id} className='hover:bg-gray-50'>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-900'>
                      {formatDateTime(log.createdAt, { dateStyle: 'short', timeStyle: 'medium' })}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${ACTION_COLORS[log.action]}`}
                      >
                        {ACTION_LABELS[log.action]}
                      </span>
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap'>
                      {log.previousStatus ? (
                        <span className={STATUS_COLORS[log.previousStatus as UserStatus]}>
                          {STATUS_LABELS[log.previousStatus as UserStatus]}
                        </span>
                      ) : (
                        <span className='text-gray-400'>-</span>
                      )}
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap'>
                      <span className={STATUS_COLORS[log.newStatus as UserStatus]}>
                        {STATUS_LABELS[log.newStatus as UserStatus]}
                      </span>
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-500'>
                      {log.suspendedUntil
                        ? formatDateTime(log.suspendedUntil, {
                            dateStyle: 'short',
                            timeStyle: 'medium'
                          })
                        : '-'}
                    </td>
                    <td className='max-w-xs truncate px-6 py-4 text-sm text-gray-500'>
                      {log.reason || '-'}
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap'>
                      <div className='flex items-center gap-2'>
                        {getPerformerIcon(log.performerType)}
                        <span className='text-gray-600'>
                          {log.performerType === 'SYSTEM'
                            ? 'システム'
                            : log.performerType === 'ADMIN'
                              ? '管理者'
                              : 'ユーザー'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className='flex items-center justify-between border-t border-gray-200 px-6 py-4'>
              <div className='text-sm text-gray-700'>
                全 {total} 件中 {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, total)}{' '}
                件を表示
              </div>
              <div className='flex gap-2'>
                <button
                  onClick={() => setUrlState({ page: Math.max(FIRST_PAGE, page - 1) })}
                  disabled={page === FIRST_PAGE}
                  className='rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  前へ
                </button>
                <span className='px-3 py-1 text-sm text-gray-600'>
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setUrlState({ page: Math.min(totalPages - 1, page + 1) })}
                  disabled={page >= totalPages - 1}
                  className='rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  次へ
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
